import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/hooks/useAuthStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useRbacReadiness } from "@/hooks/useRbacReadiness";
import { countMyFolderExclusions } from "@/stores/permissions/repository";

/**
 * Does the viewer see the whole of this project's documentation?
 *
 * "full" for owners and admins (no folder is hidden from them) and for a
 * member with no folder exclusions in the project; "partial" otherwise —
 * including while that is still being found out or could not be, because
 * analytics computed over part of a project must never pass for the project's.
 */
export type DocumentCoverage = "full" | "partial";

export interface ProjectDocumentCoverageState {
  coverage: DocumentCoverage;
  ready: boolean;
  isLoading: boolean;
  isError: boolean;
}

export const myFolderExclusionCountKey = (userProfileId: number | null, projectId: number) =>
  ["my-folder-exclusion-count", userProfileId, projectId] as const;

export function useProjectDocumentCoverage(projectId: number | null): DocumentCoverage {
  return useProjectDocumentCoverageState(projectId).coverage;
}

function loadedCoverage(exclusions: {
  isSuccess: boolean;
  data: number | undefined;
  isPending: boolean;
  isError: boolean;
}): ProjectDocumentCoverageState {
  return {
    coverage: exclusions.isSuccess && exclusions.data === 0 ? "full" : "partial",
    ready: exclusions.isSuccess,
    isLoading: exclusions.isPending,
    isError: exclusions.isError,
  };
}

function needsFolderExclusionCheck(
  isMember: boolean,
  userProfileId: number | null,
  projectId: number | null
): boolean {
  return isMember && userProfileId !== null && projectId !== null;
}

/** Distinguishes confirmed coverage from a pending or failed access check. */
export function useProjectDocumentCoverageState(
  projectId: number | null
): ProjectDocumentCoverageState {
  const auth = useAuthStore();
  const { isSubcontractorOnly } = usePermissions();
  const { ready, failed } = useRbacReadiness();
  const userProfileId = auth.userProfileId;
  const needsExclusions = needsFolderExclusionCheck(isSubcontractorOnly, userProfileId, projectId);

  const exclusions = useQuery({
    queryKey: myFolderExclusionCountKey(userProfileId, projectId ?? -1),
    queryFn: () => countMyFolderExclusions(userProfileId as number, projectId as number),
    enabled: ready && needsExclusions,
    // Exclusions change only when an admin saves this member's access; a
    // refocus is a cheap, sufficient way back to the database.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // Until roles are known, a member could be mistaken for an owner — hide.
  if (!ready) {
    return { coverage: "partial", ready: false, isLoading: !failed, isError: failed };
  }
  if (!needsExclusions) {
    return { coverage: "full", ready: true, isLoading: false, isError: false };
  }
  return loadedCoverage(exclusions);
}
